import { useRef, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { Lookup, type LookupItem, type LookupType, type LookupFacet, type LookupProps } from './Lookup'
import { Pill } from './Pill'
import { cx } from './cx'
import s from './RelationField.module.css'

/**
 * RelationField — the inline N:1 (many-to-one) control: shows the current parent
 * as a chip and opens <Lookup> (single) to change it, with clear + create-in-flow.
 * The value is the related id (serializable — definition-friendly); `display`
 * resolves an id to how it renders, and a just-picked item shows richly at once.
 */
export interface RelationFieldProps {
  /** Native id for a visible `<label htmlFor>` supplied by a composing form. */
  id?: string
  value: string | null
  /**
   * Return a PROMISE when choosing writes something, and the popover stays
   * open until it settles (#2149). Returning nothing keeps the old behaviour,
   * which is what every local-selection caller wants.
   */
  onChange: (id: string | null, selectedType: string | null) => void | Promise<unknown>
  search: LookupProps['search']
  display?: (id: string) => LookupItem
  placeholder?: string
  allowClear?: boolean
  disabled?: boolean
  types?: LookupType[]
  defaultType?: string
  onTypeChange?: (type: string) => void
  facets?: (q: string) => LookupFacet[]
  /** Governed fields offered by the shared advanced filter builder. */
  advanced?: LookupProps['advanced']
  create?: { show: (q: string) => boolean; label: (q: string) => React.ReactNode; onCreate: (q: string) => void | Promise<void> }
  /**
   * Popover width. Forwarded to <Lookup>, which clamps it to the viewport — a
   * host with less room (the capture extension's side panel, #967) asks for
   * less rather than rendering a narrower picker of its own.
   */
  width?: number
  /** Minimum query length before the governed search source is asked. */
  minChars?: number
  testid?: string
  /**
   * Accessible name for hosts that have no visible `<label htmlFor={id}>`.
   * A caller that shows a group heading rather than a label passes the same
   * words here so assistive tech reads the control, not just its placeholder.
   */
  ariaLabel?: string
  /**
   * Fail-closed state for an absent or caller-hidden target. The stored id is
   * deliberately not rendered: missing and inaccessible stay indistinguishable.
   */
  unavailableMessage?: string
}

export function RelationField({ id, value, onChange, search, display, placeholder = 'Search…', allowClear = true, disabled, types, defaultType, onTypeChange, facets, advanced, create, width, minChars, testid, ariaLabel, unavailableMessage }: RelationFieldProps) {
  const [open, setOpen] = useState(false)
  const [picked, setPicked] = useState<LookupItem | null>(null)
  const ref = useRef<HTMLButtonElement>(null)
  const item: LookupItem | null = value && !unavailableMessage
    ? (picked && picked.id === value ? picked : display?.(value) ?? { id: value, primary: 'Record unavailable' })
    : null

  return (
    <>
      {/* The clear affordance was a `span role="button" tabIndex={0}` with no
          onKeyDown, NESTED inside the field <button> — focusable but not
          activatable, and invalid HTML (#388). Two real sibling buttons in a
          styled wrapper: the field opens the lookup, the × clears. */}
      <div className={cx(s.field, !item && s.empty, (disabled || unavailableMessage) && s.fieldDisabled)} data-testid={testid}>
        <button ref={ref} id={id} type="button" disabled={disabled || !!unavailableMessage} aria-label={ariaLabel}
          className={s.open} onClick={() => !disabled && !unavailableMessage && setOpen(true)}>
          {item ? (
            <span className={s.val}>
              {item.glyph != null && <span className={s.glyph}>{item.glyph}</span>}
              <span className={s.primary}>{item.primary}</span>
              {item.badge && <Pill tone={item.badge.tone ?? 'neutral'}>{item.badge.label}</Pill>}
            </span>
          ) : <span className={s.ph}>◎ {unavailableMessage ? 'Relationship unavailable' : placeholder}</span>}
          <span className={s.grow} />
          {/* The same 15px lucide chevron Select and OptionPicker draw. A 10px
              `▾` text glyph rendered as a dot beside them (#2406 kit-defects-b). */}
          <ChevronDown size={15} className={s.caret} aria-hidden />
        </button>
        {item && allowClear && !disabled && (
          <button type="button" aria-label="Clear" className={s.clear}
            /* Clearing is a write too, but there is no surface to hold open —
               the popover is shut and the field itself stays put. The caller's
               own failure channel reports a refusal (#2149). */
            onClick={() => { setPicked(null); void onChange(null, defaultType ?? null) }}>×</button>
        )}
      </div>
      {unavailableMessage && <small className={s.unavailable} role="status">{unavailableMessage}</small>}
      {open && !unavailableMessage && (
        <Lookup presentation="popover" anchor={ref.current} search={search} types={types} defaultType={defaultType}
          onTypeChange={onTypeChange} facets={facets} advanced={advanced} create={create} width={width}
          minChars={minChars}
          testid={testid ? `${testid}-lookup` : undefined}
          onClose={() => { setOpen(false); ref.current?.focus() }}
          /* The caller's promise is RETURNED, so `Lookup` holds the popover
             open until the write settles rather than closing on the click. */
          onPick={(it, selectedType) => { setPicked(it); return onChange(it.id, selectedType) }} />
      )}
    </>
  )
}
